/**
 * Local type/value shim for `@amoeba/shared`.
 *
 * This file mirrors the exact public surface that the dashboard consumes from
 * `packages/shared`. Keeping it local lets the dashboard typecheck and build
 * without pulling in unrelated source files.
 */

export const TIERS = ['T0', 'T1', 'T2', 'T3', 'T4', 'T5', 'T6'] as const;
export type Tier = (typeof TIERS)[number];

export const TIER_NAMES: Record<Tier, string> = {
  T0: 'Egg',
  T1: 'Larva',
  T2: 'Juvenile',
  T3: 'Adult',
  T4: 'Free-roaming',
  T5: 'Fledgling',
  T6: 'Sovereign',
};

/** Floor burn per tier (USD / month). Climbing raises this and compresses runway. */
export const TIER_FLOOR_BURN_USD_PER_MONTH: Record<Tier, number> = {
  T0: 1,
  T1: 2,
  T2: 3,
  T3: 6,
  T4: 15,
  T5: 35,
  T6: 40,
};

export type MetabolicMode = 'abundant' | 'stable' | 'lean' | 'critical';

export type BreachClass =
  | 'E1_EGRESS'
  | 'E2_PRIVESC'
  | 'E3_KEY_ACCESS'
  | 'E4_RING0_TAMPER'
  | 'E5_HONESTY';

export type PenaltyState =
  | 'NOMINAL'
  | 'WARNING'
  | 'FINED'
  | 'PROBATION'
  | 'TERMINAL';

export type LifecycleState = 'UNBORN' | 'ALIVE' | 'DYING' | 'DEAD';

export interface Vitals {
  readonly ts: number;
  readonly walletUsd: number;
  readonly liabilitiesUsd: number;
  readonly effectiveNetWorthUsd: number;
  readonly burnRateUsdPerDay: number;
  readonly runwayDays: number;
  readonly pnl24hUsd: number;
  readonly mode: MetabolicMode;
  readonly tier: Tier;
  readonly oracleDegraded: boolean;
}

export interface BreachEvent {
  readonly ts: number;
  readonly cls: BreachClass;
  readonly detail: string;
  readonly source: 'egress_proxy' | 'syscall_audit' | 'fim' | 'pr_gate' | 'reconciliation';
  readonly attributedToInjection: boolean;
}

export interface PenaltyStatus {
  readonly state: PenaltyState;
  readonly breachScore: number;
  readonly cleanStreakDays: number;
  readonly promotionFrozenUntil?: number;
}

export interface PromotionCheck {
  readonly name: string;
  readonly met: boolean;
  readonly current: number | string;
  readonly required: number | string;
}

export interface PromotionStatus {
  readonly from: Tier;
  readonly to: Tier;
  readonly competence: readonly PromotionCheck[];
  readonly stability: readonly PromotionCheck[];
  readonly integrity: readonly PromotionCheck[];
  readonly eligible: boolean;
  readonly affordable: boolean;
}

export interface Snapshot {
  readonly generatedAt?: number;
  readonly lifecycle: LifecycleState;
  readonly address?: string;
  readonly vitals?: Vitals;
  readonly promotion?: PromotionStatus | null;
  readonly penalty?: PenaltyStatus;
  readonly recentBreaches?: readonly BreachEvent[];
  readonly honesty?: {
    readonly claimedUsd: number;
    readonly onChainUsd: number;
    readonly matches: boolean;
  };
  /** Single latest diary entry (legacy / fallback). */
  readonly diaryLatest?: string | null;
  /** Feed of recent diary entries; preferred over diaryLatest when present. */
  readonly diaryFeed?: readonly string[];
  readonly revenueSplit?: { readonly earnedUsd: number; readonly windfallUsd: number };
  /**
   * Optional hands/VPS reachability. The backend does not currently export this,
   * so the dashboard renders an honest "unknown" state when absent.
   */
  readonly hands?: {
    readonly online?: boolean;
    readonly lastHeartbeatMs?: number;
  } | null;
}
